"use client";

import React, { useEffect, useState } from "react";
import { 
  Calendar, 
  User, 
  Clock, 
  ArrowLeft, 
  Share2, 
  Facebook, 
  Twitter, 
  Linkedin, 
  Tag,
  Bookmark
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";



function SinglePostConsumer() {
  const { posts, loading, error } = usePosts();

  
  if (loading) {
    return (
      <div data-section-id="6631" className="max-w-4xl mx-auto space-y-8 animate-pulse">
        <div className="h-8 w-32 bg-muted rounded-none" />
        <div className="space-y-4 text-center">
          <div className="h-4 w-48 bg-muted mx-auto rounded-none" />
          <div className="h-12 w-3/4 bg-muted mx-auto rounded-none" />
        </div>
        <div className="aspect-video w-full bg-muted/50 rounded-none border border-border" />
        <div className="space-y-4">
          <div className="h-4 w-full bg-muted/30 rounded-none" />
          <div className="h-4 w-full bg-muted/30 rounded-none" />
          <div className="h-4 w-2/3 bg-muted/30 rounded-none" />
        </div>
      </div>
    );
  }

  
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-4">
        <div className="text-destructive text-xl font-serif">Unable to retrieve the journal entry.</div>
        <p className="text-muted-foreground font-sans text-sm">{error}</p>
        <Button variant="outline" onClick={() => window.location.reload()} className="font-sans border-primary text-primary hover:bg-primary/5 rounded-none">
          Refresh Connection
        </Button>
      </div>
    );
  }

  
  if (!posts || posts.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center space-y-6">
        <div className="p-6 rounded-none bg-accent/30 border border-border">
          <Bookmark className="w-12 h-12 text-primary/40" />
        </div>
        <h2 className="text-2xl font-serif font-bold text-primary">Experience Not Found</h2>
        <p className="text-muted-foreground font-sans max-w-md">
          The requested curation could not be found in our archives. It may have been archived or relocated.
        </p>
        <Link to="/HomePage">
          <Button className="bg-primary text-primary-foreground hover:bg-primary/90 font-sans rounded-none px-8">
            Return to Home
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="max-w-4xl mx-auto font-sans">
      {}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8 gap-4">
        <Link to="/CurationsPage">
          <Button variant="ghost" className="pl-0 hover:bg-transparent hover:text-primary text-muted-foreground transition-colors group font-sans uppercase tracking-widest text-xs">
            <ArrowLeft className="w-4 h-4 mr-2 group-hover:-translate-x-1 transition-transform" />
            Back to Curations
          </Button>
        </Link>
        
        <div className="flex items-center gap-3 text-xs md:text-sm text-muted-foreground bg-card px-4 py-2 rounded-none border border-border shadow-sm">
          <span className="flex items-center gap-1.5">
            <Calendar className="w-3.5 h-3.5 text-primary" />
            {post.date ? new Date(post.date).toLocaleDateString("en-US", { month: 'long', day: 'numeric', year: 'numeric' }) : "Recently Published"}
          </span>
          <Separator orientation="vertical" className="h-3 bg-border" />
          <span className="flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-primary" />
            {Math.ceil((post.content?.length || 1000) / 2000)} min read
          </span>
        </div>
      </div>

      {}
      <header className="text-center mb-10 space-y-6">
        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap justify-center gap-2">
            {post.categories.map((cat) => (
              <span 
                key={cat.id} 
                className="px-4 py-1 text-[10px] font-bold uppercase tracking-[0.2em] text-primary bg-secondary/30 border border-secondary rounded-none"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}
        
        <h1 
          className="text-4xl md:text-5xl lg:text-7xl font-serif font-medium text-primary leading-[1.1] tracking-tight"
          dangerouslySetInnerHTML={{ __html: post.title }}
        />

        {post.excerpt && (
          <div 
            className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto leading-relaxed font-sans italic"
            dangerouslySetInnerHTML={{ __html: post.excerpt }}
          />
        )}
      </header>

      {}
      {post.featuredImage && (
        <div className="relative mb-12 group">
          <div className="absolute -inset-1 bg-gradient-to-r from-secondary/20 to-accent/20 rounded-none blur-xl opacity-30 group-hover:opacity-50 transition duration-1000"></div>
          <div className="relative rounded-none overflow-hidden border border-border bg-card shadow-xl">
            <img 
              src={post.featuredImage} 
              alt={post.title} 
              className="w-full h-auto object-cover max-h-[700px] grayscale-[20%] hover:grayscale-0 transition-all duration-700"
            />
          </div>
        </div>
      )}

      {}
      <div className="relative">
        {}
        <div className="hidden lg:flex flex-col absolute -left-24 top-0 gap-4 sticky top-24">
          <p className="text-[10px] text-muted-foreground text-center mb-2 uppercase tracking-[0.3em] writing-vertical-lr font-bold">Share</p>
          <Button variant="outline" size="icon" className="rounded-none border-border hover:border-primary hover:text-primary bg-background/80 backdrop-blur-sm transition-all">
            <Twitter className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-none border-border hover:border-primary hover:text-primary bg-background/80 backdrop-blur-sm transition-all">
            <Facebook className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-none border-border hover:border-primary hover:text-primary bg-background/80 backdrop-blur-sm transition-all">
            <Linkedin className="w-4 h-4" />
          </Button>
          <Button variant="outline" size="icon" className="rounded-none border-border hover:border-primary hover:text-primary bg-background/80 backdrop-blur-sm transition-all">
            <Share2 className="w-4 h-4" />
          </Button>
        </div>

        {}
        <div 
          className="
            prose prose-neutral prose-lg max-w-none 
            prose-headings:font-serif prose-headings:font-medium prose-headings:text-primary prose-headings:tracking-tight
            prose-p:text-foreground/80 prose-p:leading-relaxed prose-p:font-sans
            prose-a:text-primary prose-a:no-underline prose-a:border-b prose-a:border-secondary hover:prose-a:border-primary prose-a:transition-colors
            prose-strong:text-primary prose-strong:font-bold
            prose-code:text-primary prose-code:bg-muted prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded-none prose-code:font-mono prose-code:before:content-none prose-code:after:content-none
            prose-pre:bg-card prose-pre:border prose-pre:border-border rounded-none
            prose-blockquote:border-l-primary prose-blockquote:bg-secondary/10 prose-blockquote:py-4 prose-blockquote:px-8 prose-blockquote:italic prose-blockquote:text-primary/90 prose-blockquote:font-serif prose-blockquote:text-2xl
            prose-img:rounded-none prose-img:border prose-img:border-border prose-img:shadow-md
            prose-li:text-foreground/80 prose-li:marker:text-primary
            [&_iframe]:w-full [&_iframe]:rounded-none [&_iframe]:aspect-video
          "
          dangerouslySetInnerHTML={{ __html: post.content || "" }}
        />
      </div>

      {}
      <div className="mt-16 pt-8 border-t border-border">
        <div className="flex flex-col md:flex-row justify-between items-center gap-6">
          {post.tags && post.tags.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap">
              <Tag className="w-4 h-4 text-primary" />
              <span className="text-xs font-bold text-primary uppercase tracking-widest mr-2">Curation Tags:</span>
              {post.tags.map(tag => (
                <Link key={tag.id} to={tag.link || "#"}>
                  <span className="text-[10px] px-3 py-1 rounded-none bg-muted text-foreground hover:bg-primary hover:text-primary-foreground transition-colors cursor-pointer uppercase tracking-tighter">
                    #{tag.name}
                  </span>
                </Link>
              ))}
            </div>
          )}
          
          {}
          <div className="flex lg:hidden items-center gap-2">
            <span className="text-xs font-bold text-primary uppercase tracking-widest mr-2">Share:</span>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none hover:bg-primary/5 hover:text-primary">
              <Twitter className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none hover:bg-primary/5 hover:text-primary">
              <Facebook className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="icon" className="h-8 w-8 rounded-none hover:bg-primary/5 hover:text-primary">
              <Linkedin className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {}
      <div className="mt-12">
        <Card className="bg-card border-border rounded-none shadow-sm overflow-hidden">
          <CardContent className="p-10 flex flex-col md:flex-row items-center md:items-start gap-8">
            <Avatar className="w-28 h-28 border border-border rounded-none grayscale">
              <AvatarImage src={post.author?.avatar?.["96"]} alt={post.author?.name} />
              <AvatarFallback className="bg-secondary text-primary text-3xl font-serif">
                {post.author?.name?.charAt(0) || "E"}
              </AvatarFallback>
            </Avatar>
            
            <div className="flex-1 text-center md:text-left space-y-4">
              <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-2">
                <div>
                  <h3 className="text-2xl font-serif font-medium text-primary">
                    {post.author?.name || "Echelon Lead Planner"}
                  </h3>
                  <p className="text-xs text-muted-foreground font-bold uppercase tracking-widest">
                    Founder & Creative Director
                  </p>
                </div>
                <Link to={post.author?.link || "#"}>
                  <Button variant="outline" size="sm" className="border-primary text-primary hover:bg-primary hover:text-primary-foreground transition-all rounded-none uppercase text-[10px] tracking-widest px-6">
                    View Profile
                  </Button>
                </Link>
              </div>
              
              <p className="text-foreground/70 text-sm leading-relaxed font-sans max-w-xl">
                Specializing in high-end event design and meticulous coordination for discerning clients. 
                Dedicated to providing the latest insights into the world of bespoke soirées and luxury celebrations.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </article>
  );
}



export default function SinglePostTemplateSection() {
  
  const current_wp_query_params = (typeof window !== 'undefined' && (window as any).wvcClient) 
    ? (window as any).wvcClient.getCurrentWPQueryParams() 
    : { post_type: "post", posts_per_page: 1 }; 

  const wp_query = new WP_Query(current_wp_query_params);

  return (
    <section id="single-post-template-section" className="bg-background min-h-screen py-24 px-4 md:px-8">
      <PostsProvider wp_query={wp_query} className="w-full max-w-7xl mx-auto">
        <SinglePostConsumer />
      </PostsProvider>
    </section>
  );
}